/**
 * HealthNexusSection.tsx
 *
 * Patient-detail "Health Nexus" tab: an explicit care-team access list.
 * The plan's doctor/coordinator/nurse are always shown (badge "From Plan",
 * computed live, not removable); any staff can add/remove extra people on top.
 * Role labels are always the account's own wording: the API resolves them for
 * the list, and the staff picker resolves them from Admin -> Roles, so nothing
 * role-named is hardcoded in this file.
 *
 * Phase 1 is informational — it shows and records who has access; it does not
 * yet gate anything.
 */
import { useCallback, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2, UserPlus } from 'lucide-react';
import { listUsers } from '@/services/admin.service';
import { useRoleTitles } from '@/hooks/useRoleTitles';
import {
  getHealthNexusAccess,
  addHealthNexusAccess,
  removeHealthNexusAccess,
} from '@/services/health-nexus.service';
import { SearchableMultiSelect } from '@/components/ui/SearchableMultiSelect';
import type { SearchableMultiSelectOption } from '@/components/ui/SearchableMultiSelect';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/store/toastStore';

interface HealthNexusSectionProps {
  patientId: number;
}

export function HealthNexusSection({ patientId }: HealthNexusSectionProps) {
  const queryClient = useQueryClient();
  const accessKey = ['patient-health-nexus-access', patientId] as const;

  const accessQuery = useQuery({
    queryKey: accessKey,
    queryFn: () => getHealthNexusAccess(patientId),
  });

  const usersQuery = useQuery({
    queryKey: ['admin-users'],
    queryFn: listUsers,
    staleTime: 5 * 60_000,
  });

  // The picker's subtitle is the person's role, worded the way this account
  // words it (Admin -> Roles). `users.role` only ever holds the canonical
  // roleType ('Doctor' / 'Coordinator' / 'Nurse' / 'Receptionist'), so it has to
  // be resolved against `aa_accounts_roles.roleTitle` here the same way the
  // API resolves the list below — otherwise an account that renamed Doctor to
  // Physician sees 'Physician' in the table and 'Doctor' in the picker.
  const { roles } = useRoleTitles();
  const roleLabel = useCallback(
    (roleType: string | null) => {
      if (!roleType || roleType === 'None') return '';
      const match = roles.find((r) => r.roleType === roleType);
      // No override configured (or the role was soft-deleted while users still
      // carry it) -> the raw type still reads sensibly.
      return match?.roleTitle?.trim() || roleType;
    },
    [roles],
  );

  const [selectedToAdd, setSelectedToAdd] = useState<string[]>([]);

  const entries = accessQuery.data ?? [];
  const existingUserIds = useMemo(
    () => new Set(entries.map((e) => e.userId)),
    [entries],
  );

  // Active staff not already on the list (plan or manual).
  const addableOptions: SearchableMultiSelectOption[] = useMemo(
    () =>
      (usersQuery.data ?? [])
        .filter((u) => u.active && !existingUserIds.has(u.userId))
        .sort((a, b) => {
          const cmp = a.lastName.localeCompare(b.lastName);
          return cmp !== 0 ? cmp : a.firstName.localeCompare(b.firstName);
        })
        .map((u) => ({
          value: String(u.userId),
          label: `${u.lastName}, ${u.firstName}`,
          subtitle: roleLabel(u.role) || u.title || undefined,
        })),
    [usersQuery.data, existingUserIds, roleLabel],
  );

  const addMutation = useMutation({
    mutationFn: async (userIds: number[]) => {
      for (const id of userIds) {
        await addHealthNexusAccess(patientId, id);
      }
    },
    onSuccess: () => {
      setSelectedToAdd([]);
      toast.success('Care team updated');
      void queryClient.invalidateQueries({ queryKey: accessKey });
    },
    onError: () => toast.error('Failed to add to care team'),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: number) => removeHealthNexusAccess(patientId, userId),
    onSuccess: () => {
      toast.success('Removed from care team');
      void queryClient.invalidateQueries({ queryKey: accessKey });
    },
    onError: () => toast.error('Failed to remove from care team'),
  });

  function handleAdd() {
    const ids = selectedToAdd.map(Number).filter((n) => n > 0);
    if (ids.length === 0) return;
    addMutation.mutate(ids);
  }

  return (
    <div className="space-y-4 py-2">
      {/*
        SCRUM-1390. At 375px a hard `w-64` staff picker plus the Add button plus the
        heading cannot share one line, so the row set the section's width and pushed
        Add off the screen edge — the title wrapping to "Health / Nexus" was the row
        squeezing it, not a heading problem. Below `md` the row wraps, the picker takes
        the full width instead of a fixed 256px, and Add sits under it.
      */}
      <div className="flex items-center justify-between gap-4 max-md:flex-wrap">
        <h2 className="text-lg font-semibold text-slate-900 max-md:whitespace-nowrap">Health Nexus</h2>
        <div className="flex items-center gap-2 max-md:w-full max-md:flex-wrap">
          <div className="w-64 max-md:w-full">
            <SearchableMultiSelect
              options={addableOptions}
              selectedValues={selectedToAdd}
              onSelectionChange={setSelectedToAdd}
              placeholder="Select staff to add…"
              searchPlaceholder="Search staff…"
              emptyMessage="No other staff available."
            />
          </div>
          <Button
            className="max-md:w-full"
            leftIcon={<UserPlus className="h-4 w-4" />}
            onClick={handleAdd}
            disabled={selectedToAdd.length === 0 || addMutation.isPending}
            loading={addMutation.isPending}
          >
            Add
          </Button>
        </div>
      </div>

      {accessQuery.isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : accessQuery.isError ? (
        <p className="py-4 text-sm text-red-600">Failed to load the Health Nexus care team.</p>
      ) : entries.length === 0 ? (
        <p className="py-4 text-sm text-slate-400 italic">there are no results</p>
      ) : (
        /*
          `overflow-hidden` clips rather than scrolls, so on a phone the ACTIONS column
          was cut off with no way to reach it ("ACTIOI" in the report). This is a raw
          table element, not the shared DataTable, so it gets no card-mode conversion
          either — `overflow-x-auto` below `md` is the minimum that makes the column
          reachable. The rounded border still needs the clip at `md` and up, hence the
          pair rather than a replacement.
        */
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm max-md:overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50">
              <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Role</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {entries.map((e) => (
                <tr key={`${e.source}-${e.userId}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-800">{e.name}</td>
                  <td className="px-4 py-3 text-slate-700">
                    <div className="flex items-center gap-2">
                      <span>{e.role}</span>
                      {e.source === 'plan' ? (
                        <Badge variant="info" size="sm">From Plan</Badge>
                      ) : (
                        <Badge variant="neutral" size="sm">Added</Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {e.source === 'manual' ? (
                      <Button
                        variant="ghost"
                        size="xs"
                        iconOnly
                        leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                        className="text-red-500 hover:bg-red-50 hover:text-red-600"
                        onClick={() => removeMutation.mutate(e.userId)}
                        disabled={removeMutation.isPending}
                        title="Remove"
                      />
                    ) : (
                      <span className="text-xs text-slate-400">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
